import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import _ from 'lodash';

import AppBar from 'components/AppBar';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import Pagination from 'components/Pagination';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import SearchWithApi from 'components/SearchWithApi';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { GroupedListModel } from 'models/groupedList';
import { htmlT } from 'utils/generic';

import {
  Container,
  Content,
  StyledButton,
  ButtonContainer,
  List,
  SubTitle,
  BoxContainer,
  ButtonGhost,
  ButtonGhostIcon,
  LoadingContainer,
} from './styles';

interface Props {
  title: string;
  subTitle?: string;
  userList: GroupedListModel[];
  isModal?: boolean;
  alreadySelected?: GroupedListModel[];
  isLoading: boolean;
  isApprovalSelection?: boolean;
  selectableType: 'arrow' | 'check' | 'radio' | 'trashcan' | null;
  onConfirmSelection: (users: GroupedListModel[]) => void;
  onCloseSelectionScreen: () => void;
  singleSelect?: boolean;
  buttonLabel?: string;
  noAdd?: boolean;
  onAdd?: () => void;
  addIcon?: string;
  addLabel?: string;
  hasFavorites?: boolean;
  onDelete?: (users: GroupedListModel[]) => void;
  loadUsers?: (currentPage: number, userName?: string) => void;
  totalUsers?: number;
  currentPage?: number;
  maxItemsPerPage?: number;
}

const UserList = ({
  title,
  subTitle,
  userList,
  isLoading,
  isModal,
  onConfirmSelection,
  onCloseSelectionScreen,
  alreadySelected,
  selectableType,
  isApprovalSelection = false,
  singleSelect,
  buttonLabel,
  noAdd = true,
  onAdd,
  addIcon,
  addLabel,
  hasFavorites,
  onDelete,
  loadUsers,
  totalUsers,
  currentPage,
  maxItemsPerPage,
}: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const [usersResult, setUsersResult] = useState<GroupedListModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<GroupedListModel[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showBottomModal, setShowBottomModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<GroupedListModel>();
  const searchResultsText = htmlT(t, 'group.groupAggregation.searchResults', { count: usersResult?.length });
  const userAccount = useUserAccount();
  const userListAfterSelected = userList.filter((user) => !selectedUser.find((selected) => user.id === selected.id));
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (alreadySelected) setSelectedUser(alreadySelected);
  }, [alreadySelected]);

  const handleSelection = (user: GroupedListModel) => {
    const userFound = selectedUser.find((item) => item.id === user.id);
    setCurrentUser(user);

    if (singleSelect) {
      if (!userFound) {
        setSelectedUser([user]);
      }
    } else {
      if (selectableType === 'arrow') {
        onConfirmSelection([user]);
      } else if (user.userGroupId && !userFound) {
        setShowBottomModal(true);
      } else if (userFound) {
        setSelectedUser(selectedUser.filter((item) => item.id !== user.id));
      } else {
        setSelectedUser([...selectedUser, user]);
      }
    }
  };

  const handleSelectionConfirm = () => {
    if (currentUser) {
      setSelectedUser([...selectedUser, currentUser]);
    }
  };

  const handleSubmitSelection = () => {
    if (selectedUser) onConfirmSelection(selectedUser);
    onCloseSelectionScreen();
  };

  const handleOnSearch = useCallback(
    (value: string) => {
      if (value) {
        setUsersResult(
          userListAfterSelected.filter(
            (item: GroupedListModel) =>
              `${item.name} ${item.lastName}`?.toLowerCase().includes(value.toLowerCase()) ||
              item.email?.toLowerCase().includes(value.toLowerCase()),
          ),
        );
        setShowSearch(true);
      } else {
        setUsersResult([]);
        setShowSearch(false);
      }
    },
    [userListAfterSelected],
  );

  const onClickDelete = (users: GroupedListModel[]) => {
    if (onDelete !== undefined) {
      onDelete(users);
    }
    return;
  };

  const handleOnSearchNew = _.debounce((value: string) => {
    setQuery(value);
    if (loadUsers !== undefined) {
      loadUsers(1, value);
    } else {
      handleOnSearch(value);
    }
  }, 750);

  return (
    <Container>
      <Mobile>
        <AppBar goBack={onCloseSelectionScreen}>
          <h5>{title}</h5>
          <SubTitle>
            {selectableType === 'arrow' && !isLoading ? t('users.subTitle', { number: userList.length }) : subTitle}
          </SubTitle>
        </AppBar>
      </Mobile>

      {isLoading && (
        <LoadingContainer>
          <Loader fullScreen={!isModal} />
        </LoadingContainer>
      )}
      <>
        <Desktop>
          {isModal ? (
            <ModalHeader title={title} onClose={onCloseSelectionScreen} />
          ) : (
            <ScreenHeader title={t('userMenu.usersProfilesTitle')} subtitle={t('userMenu.usersProfilesDetail')} />
          )}
        </Desktop>

        {!isMobile && !isModal && (
          <>
            <SearchWithApi show={true} onSearch={handleOnSearchNew} />
            <List>
              {(showSearch ? usersResult : userList).map((user) => (
                <Item
                  key={`user-${user.id}`}
                  title={`${user.name} ${user.lastName}`}
                  onClick={() => onConfirmSelection([user])}
                  groupName={user.userGroupName || t('usersList.groupTagEmpty')}
                  description={user.email}
                  groupNameActive={!!user.userGroupName}
                  deleteMode={userAccount?.type === 'admin' ? true : false}
                  forceEdit={true}
                  onDelete={() => onClickDelete([user])}
                  userType={user.type}
                />
              ))}
            </List>
          </>
        )}

        {(isMobile || isModal) && (
          <Content>
            {!noAdd && (
              <BoxContainer>
                <ButtonGhost onClick={onAdd}>
                  <ButtonGhostIcon src={addIcon} />
                  {addLabel}
                </ButtonGhost>
              </BoxContainer>
            )}
            <SearchWithApi show={true} onSearch={handleOnSearchNew} fullWidth={isModal} />

            {showSearch ? (
              <List>
                <GroupedItem
                  quantityText={searchResultsText}
                  isSearch={showSearch}
                  items={usersResult}
                  selectedItems={selectedUser}
                  selectableType={selectableType}
                  onSelection={handleSelection}
                />
              </List>
            ) : (
              <GroupedList
                isAlphabetic
                isSelectable
                items={singleSelect ? userList : userListAfterSelected}
                isSearch={showSearch}
                selectableType={selectableType}
                onSelection={handleSelection}
                currentItem={selectedUser}
                selectedQuantityKey="group.groupAggregation.userSelected"
                singleSelect={singleSelect}
                hasFavorites={hasFavorites}
              />
            )}
          </Content>
        )}

        <Pagination
          currentPage={currentPage}
          maxItemsPerPage={maxItemsPerPage}
          total={totalUsers}
          onClick={(page) => {
            loadUsers !== undefined ? loadUsers(page ?? 1, query) : null;
          }}
        />

        {((alreadySelected && alreadySelected.length > 0) || selectedUser.length > 0) && (
          <ButtonContainer onClick={handleSubmitSelection}>
            <StyledButton value={buttonLabel || t('group.groupAggregation.addUser')} />
          </ButtonContainer>
        )}

        <ConfirmBottomSheet
          show={showBottomModal}
          title={
            isApprovalSelection ? t('group.groupAggregation.selectApproverTitle') : t('group.groupAggregation.title')
          }
          content={
            isApprovalSelection
              ? htmlT(t, 'group.groupAggregation.confirmApproverMessage', { user: currentUser?.name })
              : htmlT(t, 'group.groupAggregation.userAlreadyIn', { user: currentUser?.name })
          }
          onConfirm={handleSelectionConfirm}
          onClose={() => setShowBottomModal(false)}
        />

        {selectableType === 'arrow' && (
          <FloatingButton zIndex={2} onClick={() => history.push('/usersForm')} className="target" />
        )}
      </>
    </Container>
  );
};

export default UserList;
